/**
 * Undo/redo over immutable Design snapshots.
 *
 * The design model contains no stitch coordinates and updates use structural sharing
 * (unchanged objects keep their identity), so a snapshot costs only the changed objects.
 * Rapid edits with the same `coalesceKey` (e.g. dragging, typing in a number field) are merged
 * into one undo step.
 */
export interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
  lastKey: string | null;
  lastTime: number;
}

export const HISTORY_LIMIT = 200;
export const COALESCE_MS = 800;

export function createHistory<T>(present: T): HistoryState<T> {
  return { past: [], present, future: [], lastKey: null, lastTime: 0 };
}

export function commit<T>(h: HistoryState<T>, next: T, coalesceKey: string | null = null, now = Date.now()): HistoryState<T> {
  if (next === h.present) return h;
  const merge = coalesceKey !== null && coalesceKey === h.lastKey && now - h.lastTime < COALESCE_MS;
  const past = merge ? h.past : [...h.past, h.present].slice(-HISTORY_LIMIT);
  return { past, present: next, future: [], lastKey: coalesceKey, lastTime: now };
}

/** Replaces the present without creating an undo step (e.g. loading a project). */
export function reset<T>(present: T): HistoryState<T> {
  return createHistory(present);
}

export function undo<T>(h: HistoryState<T>): HistoryState<T> {
  if (!h.past.length) return h;
  const prev = h.past[h.past.length - 1];
  return { past: h.past.slice(0, -1), present: prev, future: [h.present, ...h.future], lastKey: null, lastTime: 0 };
}

export function redo<T>(h: HistoryState<T>): HistoryState<T> {
  if (!h.future.length) return h;
  const [next, ...rest] = h.future;
  return { past: [...h.past, h.present], present: next, future: rest, lastKey: null, lastTime: 0 };
}

export const canUndo = <T>(h: HistoryState<T>): boolean => h.past.length > 0;
export const canRedo = <T>(h: HistoryState<T>): boolean => h.future.length > 0;
